import React from "react";
import { useSelector } from "react-redux";
import { PaymentTimelineProps } from "../../types/invoice.types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const PaymentTimeline: React.FC<PaymentTimelineProps> = ({
  chartData,
  today,
}) => {
  const appMode = useSelector((state: any) => state?.theme?.appMode);
  const isDark = appMode === "dark";
  // Axis labels / grid need lighter values in dark mode to stay readable
  const axisTickColor = isDark ? "#B6C2CF" : "#6B7280";
  const gridStrokeColor = isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb";
  // The main projection line + its dots need enough contrast against the dark
  // (#2A3D4F) card background; --medium-gray is too dim there.
  const lineColor = isDark ? "#9FB3C8" : "var(--medium-gray)";
  const activeDotColor = isDark ? "#E2E8F0" : "#2c3e50";
  // Dots that mark today/paid/pending sit on the dark surface, so their thin
  // ring should match the surface instead of always being white.
  const dotRing = isDark ? "#2A3D4F" : "#FFFFFF";
  // Format currency for the chart tooltip
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="p-2 border shadow-sm rounded-md"
          style={{
            backgroundColor: isDark ? "#2A3D4F" : "#FFFFFF",
            borderColor: isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb",
            color: isDark ? "#E2E8F0" : "#374151",
          }}
        >
          <p className="font-medium">{label}</p>
          <p className="text-primary">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={gridStrokeColor}
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: axisTickColor }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: axisTickColor }}
              tickFormatter={(value) => `$${value / 1000}k`}
              domain={["dataMin - 5000", "dataMax + 10000"]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ r: 4, fill: lineColor, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: activeDotColor, strokeWidth: 0 }}
            />
            {/* Add a marker for today */}
            <Line
              type="monotone"
              dataKey="current"
              stroke="transparent"
              dot={{
                r: 6,
                fill: "#EF4444",
                strokeWidth: 2,
                stroke: dotRing,
              }}
            />
            {/* Add markers for other statuses */}
            <Line
              type="monotone"
              dataKey="paid"
              stroke="transparent"
              dot={{
                r: 5,
                fill: "#047857",
                strokeWidth: 1,
                stroke: dotRing,
              }}
            />
            <Line
              type="monotone"
              dataKey="pending"
              stroke="transparent"
              dot={{
                r: 5,
                fill: "#F59E0B",
                strokeWidth: 1,
                stroke: dotRing,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div
        className="flex justify-between mt-2 text-xs"
        style={{ color: isDark ? "#B6C2CF" : "#6B7280" }}
      >
        <div>Cumulative Revenue Projection</div>
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            <div className="h-2 w-2 bg-red-500 rounded-full mr-1"></div>
            <span>Today</span>
          </div>
          <div className="flex items-center">
            <div className="h-2 w-2 bg-green-600 rounded-full mr-1"></div>
            <span>Paid</span>
          </div>
          <div className="flex items-center">
            <div className="h-2 w-2 bg-amber-500 rounded-full mr-1"></div>
            <span>Pending</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTimeline;